import * as React from "react";
import { Box, Stack, styled, Tooltip, Typography } from "@mui/material";
import QCMarkerCard, { QCMarkerCardProps } from "../../../Cards/QCMarkerCard/qc-marker-card";

import { DbMarkerSeverity } from "../../../../Core/Api/Types/db-marker-severity";
import ClipCard, { ClipCardProps } from "../../../Cards/ClipCard/clip-card";
import { Opacity } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { GetFrameRate, GoToFrame } from "../../../../Redux/Reducers/redux-slice-react-player";
import { JWPMarkerTimecode } from "../../../../Utils/Timecode/ReactPlayer";
import moment from "moment";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";
import { GetFocusedTimelineClip, SetFocusedTimelineClip } from "../../../../Redux/Reducers/redux-slice-timeline";
import { useAppSelector } from "../../../../Redux/redux-store";

export interface PlayerMarkerMarkerProps {
  originalObject: QCMarkerCardProps;
  left: number;
  width: number;
}

const PlayerMarker: React.FC<PlayerMarkerMarkerProps> = ({ left, width, originalObject }) => {
  const dispatch = useDispatch();
  const handleMarkerClick = () => {
    if (originalObject.timecodes.in.markFrame) dispatch(GoToFrame(originalObject.timecodes.in.markFrame));
  };
  return (
    <Tooltip title={<PlayerMarkerTooltip {...originalObject} />} arrow>
      <Marker status={originalObject.status} left={left} width={width} onClick={handleMarkerClick} />
    </Tooltip>
  );
};

const PlayerMarkerTooltip: React.FC<QCMarkerCardProps> = (props) => {
  return <QCMarkerCard {...props} contentOnly={true} />;
};

export interface PlayerMarkerClipProps {
  originalObject: ClipCardProps;
  left: number;
  width: number;
  focused?: boolean;
}

export const PlayerMarkerClip: React.FC<PlayerMarkerClipProps> = ({ left, width, originalObject }) => {
  const dispatch = useDispatch();
  const markerRef = React.useRef<HTMLDivElement>(null);
  const focusedClip = useAppSelector(GetFocusedTimelineClip);
  const handleMarkerClick = () => {
    if (originalObject.timecodes.in.markFrame) dispatch(GoToFrame(originalObject.timecodes.in.markFrame));
    dispatch(SetFocusedTimelineClip(originalObject));
  };
  return (
    <Tooltip
      placement="top"
      slotProps={{ popper: { modifiers: [{ name: "offset", options: { offset: [0, -4] } }] } }}
      title={<PlayerClipTooltip {...originalObject} />}
      arrow
    >
      <Marker left={left} width={width} onClick={handleMarkerClick} focused={originalObject.id === focusedClip?.id} />
    </Tooltip>
  );
};

const PlayerClipTooltip: React.FC<ClipCardProps> = (props) => {
  const { t } = useTranslation();
  const { clipName, timecodes, clipDescription, createdAt, createdBy } = props;
  const frameRate = useAppSelector(GetFrameRate);

  return (
    <Box sx={{ height: "100%", mt: 0.5, mb: 0.5, p: 2 }}>
      <Stack mr="auto">
        <Typography mb={0.5} fontWeight={500} color={"text.primary"}>
          {clipName}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <JWPMarkerTimecode frameRate={frameRate} {...timecodes.in}></JWPMarkerTimecode>
          <JWPMarkerTimecode frameRate={frameRate} {...timecodes.out}></JWPMarkerTimecode>
        </Stack>

        <Stack mt={1.5} spacing={0.5}>
          {clipDescription && (
            <Typography variant="body1" color="text.primary">
              {clipDescription}
            </Typography>
          )}
          {createdBy && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.tier">{t("common:createdBy")}:</Typography>
              <Typography color="text.secondary">{createdBy}</Typography>
            </Stack>
          )}
          {createdAt && (
            <Stack direction="row" justifyContent="space-between">
              <Typography color="text.tier">{t("common:createdAtLowerCase")}:</Typography>
              <Typography color="text.secondary">
                {moment(createdAt).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default PlayerMarker;

const Marker = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "focused",
})<{ status?: QCMarkerCardProps["status"]; focused?: boolean; left: number; width: number }>(
  ({ theme, status, left, width, focused }) => {
    let bgColor = theme.palette.primary.main;
    switch (status) {
      case DbMarkerSeverity.ERROR:
        bgColor = theme.palette.error.main;
        break;
      case DbMarkerSeverity.WARNING:
        bgColor = theme.palette.warning.main;
        break;
      case DbMarkerSeverity.INFO:
        bgColor = theme.palette.info.main;
        break;
      default:
        bgColor = theme.palette.primary.main;
    }
    return {
      position: "absolute",
      left: `${left}%`,
      top: 0,
      width: `${width}%`,
      height: 16,
      borderRadius: 3,
      background: bgColor,
      cursor: "pointer",
      overflow: "hidden",
      opacity: focused ? 1 : 0.4,
      border: focused ? `2px solid ${theme.palette.absolute.white}` : "0",
      ...theme.helpers(theme).popoverMenuHighlights,
      "&:after": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        background: theme.palette.backgroundAlpha.hover,
        opacity: 0,
        transition: "opacity .3s",
      },
      "&:hover": {
        opacity: 1,
        "&:after": {
          opacity: 1,
        },
      },
    };
  }
);
